export const TRACES_SAMPLE_RATE = 0.1;
export const POSTHOG_EU_HOST = "https://eu.i.posthog.com";

/** Errors that are expected/noise — dropped before hitting the Sentry quota. */
export const IGNORE_ERRORS: string[] = [
  "Network request failed",
  "Failed to fetch",
  "Load failed",
  "AbortError",
  "TypeError: cancelled",
  "Non-Error promise rejection captured",
];

const REDACTION = "[redacted]";

// Order matters: match the synthetic email before the bare phone digits inside it.
const PATTERNS: RegExp[] = [
  // Bearer tokens (Authorization headers, etc.)
  /Bearer\s+\S+/gi,
  // Synthetic phone email: <digits>@phone.gately.local (must run before the bare-digit rule)
  /[\w.+-]*@phone\.gately\.local/gi,
  // E.164 phone numbers, e.g. +212612345678
  /\+\d{6,15}/g,
  // Bare phone-length digit runs (no +), e.g. 212612345678 / 0612345678
  /\b\d{8,15}\b/g,
];

const SENSITIVE_KEY =
  /^(phone|phonenumber|otp|code|token|qrtoken|formtoken|authorization|email)$/i;

// The visitor pass URL `/pass/<token>`: the token IS the credential (it opens
// the pass and the self-fill form), and it rides in page URLs, transaction
// names and navigation breadcrumbs. `/rpc/pass/<procedure>` is an oRPC path,
// not a token, so it is left readable. A replace callback rather than a
// lookbehind: older iOS Safari cannot parse `(?<!…)`, and this module ships to
// the browser. The borne's phone page `/b/<qr token>` is the same kind of
// credential (it opens a visitor form); `/rpc/borne/…` never matches `/b/`.
const TOKEN_PATH = /(\/rpc)?\/(pass|b)\/[^/?#\s]+/g;

export function scrubString(input: string): string {
  let out = input.replace(
    TOKEN_PATH,
    (match, rpc: string | undefined, segment: string) =>
      rpc ? match : `/${segment}/${REDACTION}`,
  );
  for (const pattern of PATTERNS) out = out.replace(pattern, REDACTION);
  return out;
}

export function scrubEvent<T>(event: T): T {
  if (event === null || event === undefined) return event;
  walk(event as unknown, new WeakSet());
  return event;
}

// `seen` guards against circular references — Sentry transaction payloads
// contain cycles (spans point back at the parent), which would otherwise
// recurse until the stack overflows. See GATELY-SERVER-2.
function walk(node: unknown, seen: WeakSet<object>): void {
  if (node === null || typeof node !== "object") return;
  if (seen.has(node)) return;
  seen.add(node);
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      const v = node[i];
      if (typeof v === "string") node[i] = scrubString(v);
      else walk(v, seen);
    }
    return;
  }
  const obj = node as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (SENSITIVE_KEY.test(key)) {
      delete obj[key];
      continue;
    }
    const v = obj[key];
    if (typeof v === "string") obj[key] = scrubString(v);
    else walk(v, seen);
  }
}
